import { Component, h } from 'preact'
import Header from '../header'
import Axios from 'axios'
import AlertComponent from '../ui-components/AlertComponent'
import InputGroup from '../ui-components/InputGroup'
import Panel from '../panel'
import Switcher from '../control/Switcher'
import { common } from '../common'

interface Props {
  config: { [key: string]: number}
}

interface Hash<T> {
  [key: string]: T;
}

interface Modules extends Hash<boolean> {
  salesforceCrm: boolean,
  salesforceFederatedSearch: boolean,
  salesforceCaseEscalation: boolean,
  zendeskFederatedSearch: boolean,
  zendeskTicketEscalation: boolean,
  pointSystem: boolean,
  rank: boolean,
  customCss: boolean,
  thirdPartyScripts: boolean,
  group: boolean,
  gainsight: boolean,
  integrationsApi: boolean
}

interface State {
  seatLimit?: number,
  messageType?: string | null,
  message?: string,
  modules: Modules
}

class CommunityConfiguration extends Component<Props, State> {
  constructor (props: Props) {
    super(props)
    this.state = {
      ...props.config,
      messageType: null,
      message: '',
      modules: {
        salesforceCrm: common.isModuleActive('salesforceCrm'),
        salesforceFederatedSearch: common.isModuleActive('salesforceFederatedSearch'),
        salesforceCaseEscalation: common.isModuleActive('salesforceCaseEscalation'),
        zendeskFederatedSearch: common.isModuleActive('zendeskFederatedSearch'),
        zendeskTicketEscalation: common.isModuleActive('zendeskTicketEscalation'),
        pointSystem: common.isModuleActive('pointSystem'),
        rank: common.isModuleActive('rank'),
        customCss: common.isModuleActive('customCss'),
        thirdPartyScripts: common.isModuleActive('thirdPartyScripts'),
        group: common.isModuleActive('group'),
        gainsight: common.isModuleActive('gainsight'),
        integrationsApi: common.isModuleActive('integrationsApi')
      }
    }
    this.onSave = this.onSave.bind(this)
  }

  async onSave () {
    this.setState({ messageType: '' })
    const modules = Object.keys(this.state.modules) // get all modules names
      .filter((key: string) => this.state.modules[key] !== common.isModuleActive(key)) // filter out those not changed
      .reduce((result: Hash<boolean>, key: string) => { // get an object with changed modules
        result[key] = this.state.modules[key]
        return result
      }, {})
    this.setState({ messageType: null })
    try {
      await Axios.post('/settings/configuration/save', {
        config: {
          seatLimit: this.state.seatLimit || 0
        },
        modules: modules
      })

      this.setState({ messageType: 'success', message: 'Community configuration successfully saved!' })
      window.location.reload()
    } catch (err) {
      this.setState({ messageType: 'error', message: err.response.data })
    }
  }

  render ({ config }: Props, { messageType, message, seatLimit, modules }: State) {
    return (
      <div className='community-configuration-page'>
        <Header
          title='Community Configuration'
          hasBreadcrumb={false}
          saveCallback={this.onSave}
        />
        {messageType ? <AlertComponent messageType={messageType} message={message} sticky isVisibleFor={5000} /> : null}
        <div className='community-configuration'>
          <InputGroup
            title='Seat limit'
            type='number'
            onChange={(e: { target: HTMLInputElement }) => {
              this.setState({ seatLimit: parseInt(e.target.value) })
            }}
            meta='Enter the seat limit for this instance below. To set an unlimited number of seats, enter 0.'
            value={seatLimit} />
        </div>
        <h4>Modules</h4>
        <Panel title='Salesforce'>
          <Switcher
            on={modules.salesforceCrm}
            label='CRM integration'
            meta='Switching this off will disable the CRM section on self-service page in control and stop CRM integration. Already pushed data will remain in Salesforce. Switching it on will only enable self-service page.'
            onChange={() => this.setState({ modules: Object.assign(modules, { salesforceCrm: !modules.salesforceCrm }) })}
          />
          <Switcher
            on={modules.salesforceFederatedSearch}
            label='Federated search'
            meta={'Switching this off will disable the federated search section on self-service page in control and exclude salesforce KB from search. Reindexing will stop, but we\'ll not empty existing index. Switching it on will only enable self-service page.'}
            onChange={() => this.setState({ modules: Object.assign(modules, { salesforceFederatedSearch: !modules.salesforceFederatedSearch }) })}
          />
          <Switcher
            on={modules.salesforceCaseEscalation}
            label='Case escalation'
            meta='Switching this off will disable the case escalation section on self-service page in control and hide escalation button and links. Escalated topics will remain in Salesforce. Switching it on will only enable self-service page.'
            onChange={() => this.setState({ modules: Object.assign(modules, { salesforceCaseEscalation: !modules.salesforceCaseEscalation }) })}
          />
        </Panel>
        <Panel title='Gainsight'>
          <Switcher
            on={modules.gainsight}
            label='CRM integration'
            meta='Switching this off will disable the self-service page in control and stop CRM integration. Data that was pushed prior to disabling the integration will remain in Gainsight. Configuration (S3 bucket credentials) will stay. Switching it on will re-enable integration.'
            onChange={() => this.setState({ modules: Object.assign(modules, { gainsight: !modules.gainsight }) })}
          />
        </Panel>
        <Panel title='Zendesk'>
          <Switcher
            on={modules.zendeskFederatedSearch}
            label='Federated search'
            meta={'Switching this off will disable the federated search section on self-service page in control and exclude zendesk KB from search. Reindexing will stop, but we\'ll not empty existing index. Switching it on will only enable self-service page.'}
            onChange={() => this.setState({ modules: Object.assign(modules, { zendeskFederatedSearch: !modules.zendeskFederatedSearch }) })}
          />
          <Switcher
            on={modules.zendeskTicketEscalation}
            label='Ticket escalation'
            meta='Switching this off will disable the ticket escalation section on self-service page in control and hide escalation button and links. Escalated topics will remain in Zendesk. Switching it on will only enable self-service page.'
            onChange={() => this.setState({ modules: Object.assign(modules, { zendeskTicketEscalation: !modules.zendeskTicketEscalation }) })}
          />
        </Panel>
        <Panel title='Gamification'>
          <Switcher
            on={modules.pointSystem}
            label='Point system'
            meta='Switching this off will disable the self-service point system configuration page in control and disable point system rules. On destination the leaderboard widget will not be displayed in the list of sidebar widgets when c customization. If the leaderboard widget was previously added it will be displayed until  the customer removes this widget.'
            onChange={() => this.setState({ modules: Object.assign(modules, { pointSystem: !modules.pointSystem }) })}
          />
          <Switcher
            on={modules.rank}
            label='Rank system'
            meta='Switching this off will disable the self-service page in control to manage ranks. The rank icon, rank titles will not be shown as metadata text in both control and destination in all locations that the user avatar is displayed.'
            onChange={() => this.setState({ modules: Object.assign(modules, { rank: !modules.rank }) })}
          />
        </Panel>
        <Panel title='Customization'>
          <Switcher
            on={modules.customCss}
            label='Custom CSS'
            meta='Switching this off will disable the display of the option Custom CSS in Themes in the destination and hide the self service page Custom CSS in control. Previously added custom CSS will not be applied in the destination. However, previously added custom CSS is not deleted and will be applied if this module is enabled again.'
            onChange={() => this.setState({ modules: Object.assign(modules, { customCss: !modules.customCss }) })}
          />
          <Switcher
            on={modules.thirdPartyScripts}
            label='Third party scripts'
            meta='Switching this off will disable the self-service page in control to add third party scripts. Previously added custom CSS will not be applied in the destination. However, previously added third party scripts are not deleted and will be applied if this module is enabled again.'
            onChange={() => this.setState({ modules: Object.assign(modules, { thirdPartyScripts: !modules.thirdPartyScripts }) })}
          />
        </Panel>
        <Panel title='Platform'>
          <Switcher
            on={modules.group}
            label='Groups'
            meta='Switching this off will disable the self service page in control to setup and manage groups. On destination the group widget and group overview page will be hidden. All content in groups will be hidden from search.'
            onChange={() => this.setState({ modules: Object.assign(modules, { group: !modules.group }) })}
          />
        </Panel>
        <Panel title='Integrations'>
          <Switcher
            on={modules.integrationsApi}
            label='API'
            meta='Switching this off will disable the self service page in control which can be used to generate oAuth2 credentials required to connect with the inSided API. Previously generated oauth2 credentails will be revoked.'
            onChange={() => this.setState({ modules: Object.assign(modules, { integrationsApi: !modules.integrationsApi }) })}
          />
        </Panel>
      </div>
    )
  }
}

export default CommunityConfiguration
